import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  Modal,
  Input,
  Progress,
  Tooltip,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined, KeyOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import UserModal from './UserModel';
import { showSuccess, showError } from '../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../hooks/useCurrentModule';
import { useSelector } from 'react-redux';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Users {
  user_id: number;
  user_image: string;
  user_name: string;
  user_address: string;
  user_country: number;
  user_mobile: number;
  user_email: string;
  user_dob: string;
  user_role: number;
  user_status: number;
  user_organization_id?: string | null;
}

const User: React.FC = () => {
  const user = useSelector((state: any) => state.user?.user);
  const [users, setUsers] = useState<Users[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Users | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  // Reset Password State
  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [resetUser, setResetUser] = useState<Users | null>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetPasswordText, setResetPasswordText] = useState('');
  const [resetForm] = Form.useForm();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const cancel: PopconfirmProps['onCancel'] = () => {
    showError('Action cancelled');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchUser = async () => {
    try {
      const data = await apiRequest<Users[]>({
        method: 'get',
        url: 'users',
      });
      setUsers(data || []);
    } catch (error) {
      showError('Failed to fetch users');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const handleEditClick = (record: Users) => {
    setIsEditMode(true);
    setInitialValues({
      user_id: record.user_id,
      user_image: record.user_image,
      user_name: record.user_name,
      user_address: record.user_address,
      user_country: record.user_country,
      user_mobile: record.user_mobile,
      user_email: record.user_email,
      user_dob: record.user_dob,
      user_role: record.user_role,
      user_status: record.user_status,
      ...(user?.user_role === 'super-master' && {
        user_organization_id: record.user_organization_id,
      }),
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchUser();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  // Open Reset Password modal
  const handleOpenResetPassword = (record: Users) => {
    setResetUser(record);
    setResetPasswordText('');
    resetForm.resetFields();
    setResetModalVisible(true);
  };

  // Submit Password Reset
  const handleResetPasswordSubmit = async (values: any) => {
    if (!resetUser) return;
    try {
      setResetLoading(true);

      await apiRequest({
        method: 'put',
        url: 'update-user-password-direct',
        data: {
          'new_password': values.user_password
        }
      });

      showSuccess(`Password reset successfully for ${resetUser.user_name}!`);
      setResetModalVisible(false);
      setResetUser(null);
      resetForm.resetFields();
      fetchUser();
    } catch (error: any) {
      showError(error?.response?.data?.message || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  const validatePasswordStrength = (pass: string) => {
    if (!pass || pass.length < 6) return 0;
    let score = 25;
    if (/[a-z]/.test(pass)) score += 25;
    if (/[A-Z]/.test(pass)) score += 25;
    if (/\d/.test(pass) || /[^A-Za-z0-9]/.test(pass)) score += 25;
    return Math.min(score, 100);
  };

  const handleDelete = async (user_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-user/${user_id}`,
      });
      showSuccess('User deleted');
      fetchUser();
    } catch {
      showError('Failed to delete user');
    }
  };

  const handleStatusToggle = async (subjectId: number) => {
    setUsers((prev) =>
      prev.map((item) =>
        item.user_id === subjectId
          ? { ...item, user_status: item.user_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-user/${subjectId}`,
      });
      showSuccess('User status updated');
    } catch (error) {
      showError('Failed to update status');
      setUsers((prev) =>
        prev.map((item) =>
          item.user_id === subjectId
            ? { ...item, user_status: item.user_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Users>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const columns: TableColumnsType<Users> = [
    {
      title: 'Image',
      dataIndex: 'user_image',
      key: 'user_image',
      width: 100,
      render: (_, record) => (
        <img
          src={`${process.env.REACT_APP_FILE_URL}uploads/users/profile-image/${record.user_image}`}
          alt="User"
          style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
          onError={(e: any) => {
            e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(record.user_name || 'User');
          }}
        />
      ),
    },
    {
      title: 'User Details',
      key: 'user_details',
      dataIndex: 'user_details',
      width: 250,
      render: (_, record) => {
        const dob = record.user_dob
          ? new Date(record.user_dob).toLocaleDateString('en-IN', {
            timeZone: 'Asia/Kolkata',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
          : '-';
        return (
          <div>
            <div><strong>Name:</strong> {record.user_name}</div>
            <div><strong>DOB:</strong> {dob}</div>
            <div style={{ fontSize: 11, color: '#888' }}>ID: {record.user_id}</div>
          </div>
        );
      },
      filters: Array.from(new Set(users.map((u) => u.user_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.user_details || null,
      onFilter: (value, record) => record.user_name.includes(value as string),
      sorter: (a, b) => a.user_name.localeCompare(b.user_name),
      sortOrder: sortedInfo.columnKey === 'user_details' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    },
    {
      title: 'Role',
      dataIndex: 'user_role',
      key: 'user_role',
      width: 100,
      filters: Array.from(new Set(users.map((s) => s.user_role))).map((role) => ({
        text: String(role),
        value: role,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.user_role || null,
      onFilter: (value, record) => String(record.user_role).includes(String(value)),
      sorter: (a, b) => String(a.user_role).localeCompare(String(b.user_role)),
      sortOrder: sortedInfo.columnKey === 'user_role' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.user_role}</div>,
    },
    {
      title: 'Contact Info',
      key: 'contact_info',
      dataIndex: 'contact_info',
      width: 250,
      render: (_, record) => (
        <div>
          <div><strong>Mobile:</strong> {record.user_mobile || '-'}</div>
          <div><strong>Email:</strong> {record.user_email || '-'}</div>
        </div>
      ),
      filters: Array.from(new Set(users.map((s) => s.user_mobile))).map((mobile) => ({
        text: String(mobile),
        value: mobile,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.contact_info || null,
      sortOrder: sortedInfo.columnKey === 'contact_info' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    },
    {
      title: 'Address',
      key: 'user_full_address',
      dataIndex: 'user_full_address',
      width: 250,
      render: (_, record) => (
        <div>
          <div><strong>Address:</strong> {record.user_address || '-'}</div>
          <div><strong>Country:</strong> {record.user_country || '-'}</div>
        </div>
      ),
      filters: Array.from(
        new Set(users.map((s) => `${s.user_address || ''} | ${s.user_country || ''}`))
      ).map((combo) => ({
        text: combo,
        value: combo,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.user_full_address || null,
      onFilter: (value, record) =>
        `${record.user_address} | ${record.user_country}`.includes(value as string),
      sorter: (a, b) =>
        `${a.user_address} ${a.user_country}`.localeCompare(`${b.user_address} ${b.user_country}`),
      sortOrder: sortedInfo.columnKey === 'user_full_address' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    }
  ];

  // Action column with Reset Password Option
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 240,
      fixed: 'right',
      render: (_: any, record: Users) => (
        <Space size="small">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.user_status}
                onChange={() => handleStatusToggle(record.user_id)}
              />
              <Tooltip title="Edit User">
                <Button onClick={() => handleEditClick(record)} className="px-8">
                  <EditOutlined />
                </Button>
              </Tooltip>
              <Tooltip title="Reset Password">
                <Button
                  onClick={() => handleOpenResetPassword(record)}
                  className="px-8"
                  style={{ color: '#fa8c16', borderColor: '#ffd591' }}
                  icon={<UnlockOutlined />}
                />
              </Tooltip>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this user?"
              onConfirm={() => handleDelete(record.user_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Users</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add User
            </Button>
          </Flex>
        )}
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Users>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="user_id"
          dataSource={users.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: 'No user found' }}
          footer={() => (
            <Flex justify="space-between" align="center">
              <span>
                Showing {users.length === 0 ? 0 : (pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, users.length)} of{' '}
                {users.length}
              </span>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={users.length}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                onShowSizeChange={(_, size) => {
                  setPagination({ current: 1, pageSize: size });
                }}
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      {/* Main User Create/Edit Modal */}
      <UserModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />

      {/* Reset Password Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UnlockOutlined style={{ color: '#fa8c16' }} />
            <span>Reset Password</span>
          </div>
        }
        open={resetModalVisible}
        onCancel={() => {
          setResetModalVisible(false);
          setResetUser(null);
          resetForm.resetFields();
        }}
        footer={null}
        destroyOnHidden
        centered
        width={480}
      >
        {resetUser && (
          <Form
            form={resetForm}
            layout="vertical"
            onFinish={handleResetPasswordSubmit}
            style={{ marginTop: 16 }}
          >
            <div
              style={{
                background: '#f8fafc',
                padding: '12px 16px',
                borderRadius: 8,
                marginBottom: 16,
                border: '1px solid #e2e8f0',
              }}
            >
              <div style={{ fontWeight: 700, color: '#0f172a' }}>{resetUser.user_name}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Email: {resetUser.user_email || 'N/A'} | ID: #{resetUser.user_id}
              </div>
            </div>

            <Form.Item
              name="user_password"
              label="New Password"
              rules={[
                { required: true, message: 'Please input new password!' },
                { min: 6, message: 'Password must be at least 6 characters' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
                placeholder="Enter new password (min 6 characters)"
                onChange={(e) => setResetPasswordText(e.target.value)}
              />
            </Form.Item>

            {resetPasswordText && (
              <div style={{ marginBottom: 16, marginTop: -8 }}>
                <Progress
                  percent={validatePasswordStrength(resetPasswordText)}
                  showInfo={false}
                  strokeColor={
                    validatePasswordStrength(resetPasswordText) < 50
                      ? '#f5222d'
                      : validatePasswordStrength(resetPasswordText) < 75
                        ? '#fa8c16'
                        : '#52c41a'
                  }
                  size="small"
                />
                <span
                  style={{
                    fontSize: 11,
                    color:
                      validatePasswordStrength(resetPasswordText) < 50
                        ? '#f5222d'
                        : validatePasswordStrength(resetPasswordText) < 75
                          ? '#fa8c16'
                          : '#52c41a',
                    fontWeight: 600,
                  }}
                >
                  {validatePasswordStrength(resetPasswordText) < 50
                    ? 'Weak password'
                    : validatePasswordStrength(resetPasswordText) < 75
                      ? 'Medium password'
                      : 'Strong password'}
                </span>
              </div>
            )}

            <Form.Item
              name="confirm_password"
              label="Confirm New Password"
              dependencies={['user_password']}
              rules={[
                { required: true, message: 'Please confirm new password!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('user_password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: '#94a3b8' }} />}
                placeholder="Re-enter new password"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: 'right', marginTop: 24 }}>
              <Space>
                <Button
                  onClick={() => {
                    setResetModalVisible(false);
                    setResetUser(null);
                    resetForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={resetLoading}
                  style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16' }}
                >
                  Update Password
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default User;